import { useCallback, useEffect, useState } from 'react';
import {
  configurationService,
  DropdownOption,
} from '../services/configurationService';
import { api } from '../services/api';

interface UseConfigDropdownOptions {
  filterFn?: (option: DropdownOption) => boolean;
}

interface UseConfigDropdownReturn {
  options: { value: string; label: string }[];
  allOptions: DropdownOption[];
  isLoading: boolean;
  handleCreate: (name: string, metadata?: Record<string, string>) => Promise<string | undefined>;
  refresh: () => Promise<void>;
}

export function useConfigDropdown(
  key: string,
  opts?: UseConfigDropdownOptions
): UseConfigDropdownReturn {
  const [allOptions, setAllOptions] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await configurationService.getDropdownOptions(key);
      setAllOptions(data);
    } finally {
      setIsLoading(false);
    }
  }, [key]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const refresh = useCallback(async () => {
    configurationService.invalidateCache(`dropdown_${key}`);
    await fetchOptions();
  }, [key, fetchOptions]);

  const handleCreate = useCallback(
    async (name: string, metadata?: Record<string, string>): Promise<string | undefined> => {
      const value = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const nextOrder = allOptions.length + 1;

      try {
        await api.post(`/configurations/${key}/options`, {
          value,
          label: name,
          order: nextOrder,
          ...(metadata ? { metadata } : {}),
        });
        await refresh();
        return value;
      } catch (error) {
        console.error(`Failed to create option for ${key}:`, error);
        return undefined;
      }
    },
    [key, allOptions.length, refresh]
  );

  const filtered = opts?.filterFn
    ? allOptions.filter(opts.filterFn)
    : allOptions;

  const options = filtered
    .filter(o => o.isActive)
    .map(o => ({ value: o.value, label: o.label }));

  return { options, allOptions, isLoading, handleCreate, refresh };
}
